import { motion } from "framer-motion";
import { Mail, Phone, MapPin } from "lucide-react";
import { MaskedLine } from "@/components/MaskedLine";

const DETAILS = [
  { icon: Mail, label: "EMAIL", value: "ops@kbxdu.tech", href: "mailto:ops@kbxdu.tech", testid: "contact-email" },
  { icon: Phone, label: "PHONE", value: "+32 492 72 44 49", href: "tel:+32492724449", testid: "contact-phone" },
  { icon: MapPin, label: "HEADQUARTERS", value: "Schouwburgplein 8, 8500 Kortrijk", testid: "contact-hq" },
];

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="relative border-t border-orange-500/15 bg-[#070707]">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <p className="mb-4 text-[10px] tracking-[0.45em] text-orange-500/90 sm:text-xs" data-testid="contact-eyebrow">
          {"// 02 — CONTACT"}
        </p>
        <h2
          data-testid="contact-heading"
          className="font-display text-[clamp(2.75rem,9vw,7.5rem)] font-bold uppercase leading-[0.9] tracking-tight"
        >
          <MaskedLine inView>
            <span className="text-stone-100">Establish</span>
          </MaskedLine>
          <MaskedLine inView delay={0.12}>
            <span className="text-stroke-orange">Uplink</span>
          </MaskedLine>
        </h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-6 max-w-xl text-xs leading-relaxed text-stone-400 sm:text-sm"
          data-testid="contact-intro"
        >
          Programme enquiries, integration partnerships and platform assessments. Reach the
          team directly — responses are handled through secure channels.
        </motion.p>

        <div className="mt-16 border-t border-orange-500/15">
          {DETAILS.map((d, i) => (
            <motion.div
              key={d.label}
              custom={i}
              variants={itemVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              data-testid={d.testid}
              className="group flex flex-col gap-2 border-b border-orange-500/15 py-6 transition-colors duration-300 hover:bg-orange-500/[0.04] sm:flex-row sm:items-center sm:justify-between sm:px-4"
            >
              <span className="flex items-center gap-3 text-[10px] tracking-[0.35em] text-stone-500">
                <d.icon className="h-4 w-4 text-orange-500/70" strokeWidth={1.5} />
                {d.label}
              </span>
              {d.href ? (
                <a
                  href={d.href}
                  data-testid={`${d.testid}-link`}
                  className="break-all font-display text-lg font-semibold uppercase tracking-wide text-stone-100 transition-colors duration-300 group-hover:text-orange-500 sm:text-xl"
                >
                  {d.value}
                </a>
              ) : (
                <span className="font-display text-lg font-semibold uppercase tracking-wide text-stone-100 transition-colors duration-300 group-hover:text-orange-500 sm:text-xl">
                  {d.value}
                </span>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      <footer className="border-t border-orange-500/15">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-6 text-[9px] tracking-[0.3em] text-stone-600 sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:text-[10px]">
          <span data-testid="footer-brand">© 2026 KB<span className="text-orange-500/70">/</span>XDU — ALL RIGHTS RESERVED</span>
          <span data-testid="footer-company">KB/XDU BY GE COMPANY</span>
          <span data-testid="footer-tagline">SPECIAL MISSION ELECTRONICS INTEGRATION</span>
          <span data-testid="footer-note">KORTRIJK, BELGIUM</span>
        </div>
      </footer>
    </section>
  );
}
